/**
 * Meeting Validation Utilities
 * Provides smart validation for meeting conflicts and participant availability
 */

/**
 * Check if two time ranges overlap
 * @param {string} start1 - Start time 1 (HH:MM format)
 * @param {string} end1 - End time 1 (HH:MM format)
 * @param {string} start2 - Start time 2 (HH:MM format)
 * @param {string} end2 - End time 2 (HH:MM format)
 * @returns {boolean} - True if times overlap
 */
export const doTimesOverlap = (start1, end1, start2, end2) => {
  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const start1Min = timeToMinutes(start1);
  const end1Min = timeToMinutes(end1);
  const start2Min = timeToMinutes(start2);
  const end2Min = timeToMinutes(end2);

  // Check for overlap: start1 < end2 AND end1 > start2
  return start1Min < end2Min && end1Min > start2Min;
};

/**
 * Check if a date is today
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {boolean} - True if date is today
 */
export const isToday = (date) => {
  const today = new Date().toISOString().split('T')[0];
  return date === today;
};

/**
 * Check if a meeting is currently ongoing
 * @param {Object} meeting - Meeting object
 * @returns {boolean} - True if meeting is ongoing
 */
export const isMeetingOngoing = (meeting) => {
  // Check both status and state fields for backward compatibility
  if (meeting.status !== 'ongoing' && meeting.state !== 'ongoing') return false;
  
  const today = new Date().toISOString().split('T')[0];
  if (meeting.scheduled_date !== today) return false;
  
  const now = new Date();
  const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  
  return currentTime >= meeting.start_time && currentTime <= meeting.end_time;
};

/**
 * Find conflicting participants for a new meeting
 * @param {Array} participants - Selected participant IDs
 * @param {Array} ongoingMeetings - List of ongoing meetings
 * @param {string} upcomingDate - Meeting date (YYYY-MM-DD)
 * @param {string} startTime - Meeting start time (HH:MM)
 * @param {string} endTime - Meeting end time (HH:MM)
 * @param {Array} allUsers - All users list
 * @param {number} excludeMeetingId - Meeting ID to exclude from conflicts (for updates)
 * @returns {Array} - Array of conflicting participants with details
 */
export const findConflictingParticipants = (participants, ongoingMeetings, upcomingDate, startTime, endTime, allUsers, excludeMeetingId = null) => {
  const conflicts = [];
  
  // Only check for conflicts on the same date
  const sameDayMeetings = ongoingMeetings.filter(meeting => 
    meeting.scheduled_date === upcomingDate && 
    (meeting.status === 'ongoing' || meeting.state === 'ongoing') &&
    meeting.id !== excludeMeetingId // Exclude current meeting for updates
  );
  
  participants.forEach(participantId => {
    const user = allUsers.find(u => u.id === participantId);
    if (!user) return;
    
    const userConflicts = sameDayMeetings.filter(meeting => {
      // Check if user is participant in this meeting
      const isParticipant = meeting.participants?.some(p => p.id === participantId);
      if (!isParticipant) return false;
      
      // Check if times overlap
      return doTimesOverlap(startTime, endTime, meeting.start_time, meeting.end_time);
    });
    
    if (userConflicts.length > 0) {
      conflicts.push({
        user,
        conflicts: userConflicts.map(meeting => ({
          id: meeting.id,
          title: meeting.title,
          start_time: meeting.start_time,
          end_time: meeting.end_time,
          status: meeting.status
        }))
      });
    }
  });
  
  return conflicts;
};

/**
 * Get participant availability status
 * @param {number} userId - User ID
 * @param {Array} ongoingMeetings - List of ongoing meetings
 * @param {string} upcomingDate - Meeting date (YYYY-MM-DD)
 * @param {string} startTime - Meeting start time (HH:MM)
 * @param {string} endTime - Meeting end time (HH:MM)
 * @returns {Object} - Availability status object
 */
export const getParticipantAvailability = (userId, ongoingMeetings, upcomingDate, startTime, endTime) => {
  const sameDayMeetings = ongoingMeetings.filter(meeting => 
    meeting.scheduled_date === upcomingDate && 
    (meeting.status === 'ongoing' || meeting.state === 'ongoing')
  );
  
  const conflictingMeetings = sameDayMeetings.filter(meeting => {
    const isParticipant = meeting.participants?.some(p => p.id === userId);
    if (!isParticipant) return false;
    
    return doTimesOverlap(startTime, endTime, meeting.start_time, meeting.end_time);
  });
  
  return {
    isAvailable: conflictingMeetings.length === 0,
    conflictingMeetings,
    conflictCount: conflictingMeetings.length
  };
};

/**
 * Format time for display
 * @param {string} time - Time in HH:MM format
 * @returns {string} - Formatted time
 */
export const formatTime = (time) => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

/**
 * Generate conflict message for display
 * @param {Object} conflict - Conflict object
 * @returns {string} - Formatted conflict message
 */
export const generateConflictMessage = (conflict) => {
  const meetingList = conflict.conflicts.map(meeting => 
    `"${meeting.title}" (${formatTime(meeting.start_time)} - ${formatTime(meeting.end_time)})`
  ).join(', ');
  
  const userName = conflict.user?.full_name || conflict.user?.name || conflict.user?.email || 'Unknown user';
  return `${userName}: ${meetingList}`;
};
